import { useEffect, useState } from "react";

// Minimal hash router — matches the Survantic convention of parsing
// window.location.hash instead of pulling in react-router.
// Routes look like: #/, #/docs, #/docs/<category>/<slug>, #/downloads, #/chat, #/login

export interface Route {
  /** Path segments after the leading "#/", e.g. ["docs", "edge", "install"]. */
  segments: string[];
  /** The full normalized path, e.g. "/docs/edge/install". */
  path: string;
}

function parseHash(): Route {
  const raw = window.location.hash.replace(/^#/, "");
  // Drop any query string (e.g. "?next=/chat") before splitting into segments.
  const pathOnly = raw.split("?")[0];
  const path = pathOnly.startsWith("/") ? pathOnly : `/${pathOnly}`;
  const segments = path.split("/").filter(Boolean);
  return { segments, path: `/${segments.join("/")}` };
}

export function useHashRoute(): Route {
  const [route, setRoute] = useState<Route>(parseHash);
  useEffect(() => {
    const onChange = () => setRoute(parseHash());
    window.addEventListener("hashchange", onChange);
    return () => window.removeEventListener("hashchange", onChange);
  }, []);
  return route;
}

export function navigate(path: string): void {
  const clean = path.startsWith("/") ? path : `/${path}`;
  window.location.hash = clean;
}

/** Anchor that navigates via the hash router. */
export function Link({
  to,
  className,
  children,
  onClick,
}: {
  to: string;
  className?: string;
  children: React.ReactNode;
  onClick?: () => void;
}) {
  return (
    <a
      href={`#${to.startsWith("/") ? to : `/${to}`}`}
      className={className}
      onClick={onClick}
    >
      {children}
    </a>
  );
}
